<template>
  <div class="d-flex h-100 flex-column">
    <div class="flex-grow-1 justify-content-end p-3 overflow-auto pretty-scroll-theme" style="height: 1px;"
      id="voting_rounds">
      <details open>
        <summary class="justify-content-between align-items-center mb-3" style="border-bottom: 2px solid #9B9F9B">
          <span class="h-5 text-white-50">
            {{ $t("js.voting_rounds.active_rounds") }}
          </span>
          <span class="badge text-bg-info float-end">{{ openRounds.length }}</span>
        </summary>
        <template v-if="openRounds.length > 0">
          <VotingRound v-for="votingRound in openRounds" :selected="votingRound.id === selectedRound"
            :votingRound="votingRound" :key="`voting_round_${votingRound.reference}`"
            :roundState="calculateState(votingRound)" @toggleRound="toggleRound" />
        </template>
        <div v-else class="text-center p-3 text-white-50">{{ $t("js.voting_rounds.no_active_rounds") }}</div>
      </details>

      <details>
        <summary class="justify-content-between align-items-center mb-3" style="border-bottom: 2px solid #9B9F9B">
          <span class="h-5 text-white-50">
            {{ $t("js.voting_rounds.inactive_rounds") }}
          </span>
          <span class="badge float-end text-bg-warning">{{ availableRounds.length }}</span>
        </summary>
        <template v-if="availableRounds.length > 0">
          <VotingRound v-for="votingRound in availableRounds" :selected="votingRound.id === selectedRound"
            :votingRound="votingRound" :key="`voting_round_${votingRound.reference}`"
            :roundState="calculateState(votingRound)" @toggleRound="toggleRound" />
        </template>
        <div v-else class="text-center p-3 text-white-50">{{ $t("js.voting_rounds.no_inactive_rounds") }} </div>
      </details>

      <details open>
        <summary class="justify-content-between align-items-center mb-3" style="border-bottom: 2px solid #9B9F9B">
          <span class="h-5 text-white-50">
            {{ $t("js.voting_rounds.closed_rounds") }}
          </span>
          <span class="badge text-bg-warning float-end">{{ closedRounds.length }}</span>
        </summary>
        <template v-if="closedRounds.length > 0">
          <VotingRound v-for="votingRound in closedRounds" :selected="votingRound.id === selectedRound"
            :votingRound="votingRound" :key="`voting_round_${votingRound.reference}`"
            :roundState="calculateState(votingRound)" @toggleRound="toggleRound" />
        </template>
      </details>

      <details open>
        <summary class="justify-content-between align-items-center mb-3" style="border-bottom: 2px solid #9B9F9B">
          <span class="h-5 text-white-50">
            {{ $t("js.voting_rounds.finished_rounds") }}
          </span>
          <span class="badge text-bg-success float-end">{{ finishedRounds.length }}</span>
        </summary>
        <template v-if="finishedRounds.length > 0">
          <VotingRound v-for="(votingRound, index) in finishedRounds" :selected="votingRound.id === selectedRound"
            :votingRound="votingRound" :key="`voting_round_${votingRound.reference}_${index}`"
            :roundState="calculateState(votingRound)" @toggleRound="toggleRound" />
        </template>
      </details>
    </div>
  </div>

</template>

<script lang="ts">
import { mapState } from "pinia";
import { defineComponent } from "vue";
import { calculateState } from "@/entrypoints/shared/voting_rounds_helper";
import VotingRound from "./VotingRound.vue";
import { usePresentationStore } from "@/entrypoints/stores/presentation";
import type { ConferenceVotingRound, E2E } from "@/types";

export default defineComponent({
  name: "VotingRounds",
  components: { VotingRound },
  props: {
    selectedRound: {
      type: Number,
      default: null,
    },
  },
  computed: {
    ...mapState(usePresentationStore, ["votingRounds", "votingRoundReports"]),
    filteredVotingRounds() {
      return this.votingRounds.filter((vr: ConferenceVotingRound) => vr.managedBySlide)
    },
    closedRounds() {
      return this.filteredVotingRounds.filter((vr: ConferenceVotingRound) =>
        ["closed", "past"].includes(calculateState(vr, this.fetchVotingRoundReport(vr))));
    },
    availableRounds() {
      return this.filteredVotingRounds.filter((vr: ConferenceVotingRound) =>
        ["available"].includes(calculateState(vr)));
    },
    openRounds() {
      return this.filteredVotingRounds.filter((vr: ConferenceVotingRound) =>
        ["open", "active"].includes(calculateState(vr)));
    },
    finishedRounds() {
      return this.filteredVotingRounds.filter((vr: ConferenceVotingRound) =>
        ["finished"].includes(calculateState(vr, this.fetchVotingRoundReport(vr))));
    },
  },
  methods: {
    calculateState,
    toggleRound(votingRoundId: number) {
      this.$emit("toggleRound", votingRoundId);
    },
    fetchVotingRoundReport(vr: ConferenceVotingRound) {
      return this.votingRoundReports?.find((vrr: E2E.VotingRoundReport) =>
        vrr.votingRoundReference === vr.reference);
    },
  },
});
</script>
